import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Mail, Loader2, AlertTriangle, Car, ShieldAlert, Clock, Euro, HandCoins, Wallet } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Loading, Empty, Plate, Chip, ExportBtn, FilterBar, Field, NSelect, Kpi, Panel, useFetch } from "@/components/common";
import { Input } from "@/components/ui/input";
import { useLivOptions } from "@/pages/livraison/LivDashboard";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fDate, fEur } from "@/lib/api";

function TestMailButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/accidents/notify-test");
      data.results.forEach((r) => (r.status === "envoye" ? toast.success(`E-mail de test envoyé à ${r.to}`) : toast.error(`Échec de l'envoi à ${r.to}`)));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={run} disabled={busy} className="h-10 px-4 rounded-lg border border-slate-300 bg-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-slate-50" data-testid="accident-test-mail-button">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Envoyer un e-mail de test
    </button>
  );
}

export const ACC_STATUS = {
  nouveau: ["Nouveau", "bg-blue-100 text-blue-800 border-blue-300"],
  a_completer: ["À compléter", "bg-amber-100 text-amber-800 border-amber-300"],
  declare_assurance: ["Déclaré à l'assurance", "bg-violet-100 text-violet-800 border-violet-300"],
  expertise_attente: ["Expertise en attente", "bg-orange-100 text-orange-800 border-orange-300"],
  devis_attente: ["Devis en attente", "bg-orange-50 text-orange-700 border-orange-200"],
  reparation_programmee: ["Réparation programmée", "bg-cyan-100 text-cyan-800 border-cyan-300"],
  reparation_effectuee: ["Réparation effectuée", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  remboursement_attente: ["Remboursement en attente", "bg-yellow-100 text-yellow-800 border-yellow-300"],
  rembourse: ["Remboursé", "bg-green-100 text-green-800 border-green-300"],
  clos: ["Clos", "bg-slate-200 text-slate-700 border-slate-300"],
};
export const RESP = { oui: "Oui", non: "Non", a_determiner: "À déterminer" };
export const REFUND = { recu: ["Reçu", "bg-emerald-50 text-emerald-700 border-emerald-200"], attente: ["En attente", "bg-amber-50 text-amber-700 border-amber-200"], non: ["Non attendu", "bg-slate-50 text-slate-600 border-slate-200"] };
export const AccStatus = ({ v }) => <Chip cls={ACC_STATUS[v]?.[1]} testid="accident-status-chip">{ACC_STATUS[v]?.[0] || v}</Chip>;

const defLink = (a) => (a.module === "commercial" ? `/commerciaux/accidents/${a.id}` : `/livraison/accidents/${a.id}`);

export function AccidentsTable({ rows, admin, showVehicle = true, showDriver = true, linkTo = defLink, driverLabel = "Conducteur" }) {
  if (!rows.length) return <Empty text="Aucun accident / sinistre" />;
  return (
    <div className="overflow-x-auto">
      <table className="tbl w-full" data-testid="accidents-table">
        <thead><tr><th>N°</th><th>Date</th>{showVehicle && <th>Véhicule</th>}{showDriver && <th>{driverLabel}</th>}<th>Résumé</th><th>Responsable</th><th>Roulant</th><th>Statut</th>
          {admin && <><th>N° assurance</th><th>Remboursement</th><th className="text-right">Coût</th><th className="text-right">Franchise</th></>}</tr></thead>
        <tbody>{rows.map((a) => (
          <tr key={a.id} data-testid={`accident-row-${a.number}`}>
            <td><Link to={linkTo(a)} className="font-mono font-bold text-[#0E2D43] hover:underline" data-testid={`accident-link-${a.number}`}>{a.number}</Link></td>
            <td>{fDate(a.date)} <span className="text-slate-400">{a.time}</span></td>
            {showVehicle && <td><Plate>{a.plate}</Plate> <span className="text-xs text-slate-500">{a.vehicle_label}</span></td>}
            {showDriver && <td>{a.driver_name}</td>}
            <td className="max-w-[260px] truncate">{a.location} — {a.description}</td>
            <td>{RESP[a.responsible]}</td>
            <td>{a.drivable ? "Oui" : <Chip cls="bg-rose-50 text-rose-700 border-rose-200">Immobilisé</Chip>}</td>
            <td><AccStatus v={a.status} /></td>
            {admin && <>
              <td className="font-mono text-xs">{a.insurance_number || "—"}</td>
              <td>{a.refund_state ? <Chip cls={REFUND[a.refund_state][1]}>{REFUND[a.refund_state][0]}</Chip> : "—"}</td>
              <td className="text-right tabular-nums">{fEur(a.cost)}</td>
              <td className="text-right tabular-nums">{fEur(a.deductible)}</td>
            </>}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const ALERT_ICONS = { not_declared: ShieldAlert, no_number: ShieldAlert, expertise: Clock, repair: Clock, refund: HandCoins, deductible: Euro, too_old: AlertTriangle };

export function AccOverview({ module, withKpis = true }) {
  const { data } = useFetch("/accidents-overview", { module });
  if (!data) return null;
  const k = data.kpis;
  const al = Object.entries(data.alerts).filter(([, l]) => l.length);
  return (
    <div className="mb-6 space-y-4" data-testid={`acc-overview-${module}`}>
      {withKpis && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi testid="acc-kpi-open" label="Sinistres ouverts" value={k.open} icon={AlertTriangle} tone="danger" />
          <Kpi testid="acc-kpi-immob" label="Véhicules immobilisés" value={k.immobilized} icon={Car} tone="danger" delay={1} />
          <Kpi testid="acc-kpi-ins" label="Dossiers assurance en attente" value={k.insurance_pending} icon={ShieldAlert} delay={2} />
          <Kpi testid="acc-kpi-refund" label="Remboursements en attente" value={k.refund_pending} icon={HandCoins} delay={3} />
          <Kpi testid="acc-kpi-deductible" label={`Franchises ${data.year}`} value={fEur(k.deductible_year, 0)} icon={Euro} tone="navy" delay={4} />
          <Kpi testid="acc-kpi-refunded" label={`Remboursé par l'assurance ${data.year}`} value={fEur(k.refunded_year, 0)} icon={HandCoins} tone="navy" delay={5} />
          <Kpi testid="acc-kpi-remaining" label={`Reste à charge Prest'hyg ${data.year}`} value={fEur(k.remaining_year, 0)} icon={Wallet} tone="navy" delay={6} />
        </div>
      )}
      <Panel title="Alertes Direction — sinistres" testid="acc-alerts">
        {!al.length ? <p className="text-sm text-slate-500">Aucune alerte.</p> : (
          <ul className="space-y-2">{al.map(([key, list]) => {
            const I = ALERT_ICONS[key];
            return (
              <li key={key} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`acc-alert-${key}`}>
                <I className="w-4 h-4 text-[#E0830B]" /><b>{data.alert_labels[key]}</b><Chip>{list.length}</Chip>
                {list.slice(0, 8).map((a) => <Link key={a.id} to={module === "commercial" ? `/commerciaux/accidents/${a.id}` : `/livraison/accidents/${a.id}`} className="font-mono text-xs text-[#0E2D43] underline">{a.number}</Link>)}
              </li>
            );
          })}</ul>
        )}
      </Panel>
    </div>
  );
}

function useM2Options(on) {
  const v = useFetch(on ? "/m2/vehicles" : null);
  const u = useFetch(on ? "/commerciaux" : null);
  return {
    vehicles: (v.data || []).map((x) => ({ value: x.id, label: `${x.plate} · ${x.brand} ${x.model}` })),
    drivers: (u.data || []).map((x) => ({ value: x.id, label: x.name })),
  };
}

function MySinistres() {
  const { data, loading } = useFetch("/m2/sinistres");
  return (
    <div data-testid="accidents-page">
      <PageHeader overline="Véhicules commerciaux" title="Mes accidents / sinistres">
        <Link to="/commerciaux/espace?tab=sinistres" className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold" data-testid="accident-declare-button"><Plus className="w-4 h-4" /> Déclarer un sinistre</Link>
      </PageHeader>
      <div className="bg-white border border-slate-200 rounded-xl rise">{loading ? <Loading /> : <AccidentsTable rows={data || []} showDriver={false} linkTo={(a) => `/commerciaux/sinistres/${a.id}`} />}</div>
    </div>
  );
}

export default function Accidents({ module = "livraison" }) {
  const { user } = useAuth();
  const com = module === "commercial";
  if (com && user.role !== "admin") return <MySinistres />;
  return <AccidentsAdmin module={module} />;
}

function AccidentsAdmin({ module }) {
  const { user } = useAuth();
  const com = module === "commercial";
  const admin = user.role === "admin";
  const full = admin && !["chef_depot", "chauffeur"].includes(user.real_role);
  const [f, setF] = useState({});
  const lo = useLivOptions();
  const mo = useM2Options(com);
  const o = com ? mo : lo;
  const dk = com ? "user_id" : "driver_id";
  const { data, loading } = useFetch("/accidents", { ...f, module });
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  return (
    <div data-testid="accidents-page">
      <PageHeader overline={com ? "Véhicules commerciaux" : "Véhicules de livraison"} title="Accidents / Sinistres">
        {admin && <ExportBtn path="/export/accidents" params={{ ...f, module }} filename={com ? "sinistres-commerciaux.xlsx" : "accidents.xlsx"} testid="export-accidents" />}
        {user.canEdit && !com && <TestMailButton />}
        {user.real_role !== "direction" && !com && <Link to="/livraison/accidents/nouveau" className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] text-sm font-bold" data-testid="accident-declare-button"><Plus className="w-4 h-4" /> Déclarer un accident</Link>}
      </PageHeader>
      {full && <AccOverview module={module} withKpis={com} />}
      <FilterBar testid="accidents-filters">
        <Field label="Du"><Input type="date" value={f.date_from || ""} onChange={set("date_from")} className="h-10 bg-white" data-testid="acc-filter-from" /></Field>
        <Field label="Au"><Input type="date" value={f.date_to || ""} onChange={set("date_to")} className="h-10 bg-white" data-testid="acc-filter-to" /></Field>
        <Field label="Véhicule"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} options={o.vehicles} placeholder="Tous" testid="acc-filter-vehicle" /></Field>
        <Field label={com ? "Conducteur" : "Chauffeur"}><NSelect value={f[dk]} onChange={set(dk)} options={o.drivers} placeholder="Tous" testid="acc-filter-driver" /></Field>
        <Field label="Statut"><NSelect value={f.status} onChange={set("status")} placeholder="Tous" testid="acc-filter-status"
          options={[{ value: "ouverts", label: "Ouverts (non clos)" }, ...Object.entries(ACC_STATUS).map(([k, x]) => ({ value: k, label: x[0] }))]} /></Field>
        <Field label="Responsable"><NSelect value={f.responsible} onChange={set("responsible")} placeholder="Tous" testid="acc-filter-responsible"
          options={Object.entries(RESP).map(([k, l]) => ({ value: k, label: l }))} /></Field>
        {full && <Field label="Assurance"><Input value={f.insurer || ""} onChange={set("insurer")} placeholder="Assureur" className="h-10 bg-white" data-testid="acc-filter-insurer" /></Field>}
        {full && <Field label="Remboursement effectué"><NSelect value={f.refunded} onChange={set("refunded")} placeholder="Tous" testid="acc-filter-refunded" options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field>}
      </FilterBar>
      <div className="bg-white border border-slate-200 rounded-xl rise">{loading ? <Loading /> : <AccidentsTable rows={data || []} admin={full} driverLabel={com ? "Conducteur" : "Chauffeur"} />}</div>
    </div>
  );
}
